import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Target, ArrowRight, Ruler } from 'lucide-react';
import { updateProfile } from '../lib/api';
import { toast } from 'sonner';

const kgToLb = (kg) => Math.round(kg * 2.20462 * 10) / 10;
const lbToKg = (lb) => Math.round((lb / 2.20462) * 10) / 10;
const cmToInch = (cm) => Math.round(cm / 2.54);
const inchToCm = (inch) => Math.round(inch * 2.54);

const OnboardingModal = ({ open, onClose, onSaved }) => {
  const [step, setStep] = useState(0);
  const [units, setUnits] = useState('metric');
  const [sex, setSex] = useState('female');
  const [age, setAge] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [currentKg, setCurrentKg] = useState('');
  const [currentLb, setCurrentLb] = useState('');
  const [goalKg, setGoalKg] = useState('');
  const [goalLb, setGoalLb] = useState('');
  const [activity, setActivity] = useState('light');
  const [pace, setPace] = useState('steady');
  const [saving, setSaving] = useState(false);

  const finalWeightKg = useMemo(() => units === 'metric' ? parseFloat(currentKg) : (currentLb ? lbToKg(parseFloat(currentLb)) : 0), [units, currentKg, currentLb]);
  const finalGoalKg   = useMemo(() => units === 'metric' ? parseFloat(goalKg)    : (goalLb ? lbToKg(parseFloat(goalLb)) : 0), [units, goalKg, goalLb]);
  const finalHeightCm = useMemo(() => units === 'metric' ? parseFloat(heightCm) : (heightIn ? inchToCm(parseFloat(heightIn)) : 0), [units, heightCm, heightIn]);

  const direction = finalGoalKg && finalWeightKg ? (finalGoalKg < finalWeightKg - 0.5 ? 'lose' : finalGoalKg > finalWeightKg + 0.5 ? 'gain' : 'maintain') : 'maintain';

  const canNext = () => {
    if (step === 0) return !!units && !!sex && parseInt(age) >= 13;
    if (step === 1) return finalHeightCm > 80 && finalWeightKg > 20;
    if (step === 2) return finalGoalKg > 20;
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateProfile({
        current_weight_kg: finalWeightKg,
        goal_weight_kg: finalGoalKg,
        height_cm: finalHeightCm,
        age: parseInt(age),
        sex,
        activity_level: activity,
        goal_pace: direction === 'maintain' ? 'maintain' : pace,
        unit_preference: units,
      });
      toast.success(`Daily target: ${res.daily_target_calories} kcal`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-2"><Target className="w-6 h-6" /></div>
          <DialogTitle className="font-display text-2xl text-emerald-950">Let’s personalise your target</DialogTitle>
          <DialogDescription>Takes 30 seconds. We’ll calculate your daily calories & macros.</DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-5 pt-2">
            <div>
              <Label className="text-emerald-950">Units</Label>
              <RadioGroup value={units} onValueChange={setUnits} className="flex gap-4 mt-2">
                <label className={`flex-1 rounded-xl border p-3 text-center cursor-pointer ${units==='metric' ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-950/10'}`}><RadioGroupItem value="metric" className="sr-only" /><span className="font-medium text-emerald-950">Metric</span><span className="text-emerald-950/50 text-xs block">kg, cm</span></label>
                <label className={`flex-1 rounded-xl border p-3 text-center cursor-pointer ${units==='imperial' ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-950/10'}`}><RadioGroupItem value="imperial" className="sr-only" /><span className="font-medium text-emerald-950">Imperial</span><span className="text-emerald-950/50 text-xs block">lb, in</span></label>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-emerald-950">Sex</Label>
              <RadioGroup value={sex} onValueChange={setSex} className="flex gap-4 mt-2">
                <label className={`flex-1 rounded-xl border p-3 text-center cursor-pointer ${sex==='female' ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-950/10'}`}><RadioGroupItem value="female" className="sr-only" /><span className="font-medium text-emerald-950">Female</span></label>
                <label className={`flex-1 rounded-xl border p-3 text-center cursor-pointer ${sex==='male' ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-950/10'}`}><RadioGroupItem value="male" className="sr-only" /><span className="font-medium text-emerald-950">Male</span></label>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="age" className="text-emerald-950">Age</Label>
              <Input id="age" type="number" min="13" max="100" value={age} onChange={(e) => setAge(e.target.value)} className="mt-2 h-11" placeholder="28" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5 pt-2">
            <div>
              <Label className="text-emerald-950 flex items-center gap-2"><Ruler className="w-4 h-4" />Height</Label>
              {units === 'metric' ? (
                <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} placeholder="170" className="mt-2 h-11" />
              ) : (
                <Input type="number" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} placeholder="67" className="mt-2 h-11" />
              )}
              <p className="text-xs text-emerald-950/50 mt-1">{units === 'metric' ? 'centimetres' : 'inches (5’7” ≈ 67 in)'}</p>
            </div>
            <div>
              <Label className="text-emerald-950">Current weight</Label>
              {units === 'metric' ? (
                <Input type="number" step="0.1" value={currentKg} onChange={(e) => setCurrentKg(e.target.value)} placeholder="72.5" className="mt-2 h-11" />
              ) : (
                <Input type="number" step="0.1" value={currentLb} onChange={(e) => setCurrentLb(e.target.value)} placeholder="160" className="mt-2 h-11" />
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 pt-2">
            <div>
              <Label className="text-emerald-950">Goal weight</Label>
              {units === 'metric' ? (
                <Input type="number" step="0.1" value={goalKg} onChange={(e) => setGoalKg(e.target.value)} placeholder="65" className="mt-2 h-11" />
              ) : (
                <Input type="number" step="0.1" value={goalLb} onChange={(e) => setGoalLb(e.target.value)} placeholder="145" className="mt-2 h-11" />
              )}
              {finalWeightKg > 0 && finalGoalKg > 0 && (<p className="text-xs text-emerald-950/60 mt-1">{direction === 'lose' ? `Losing ${(finalWeightKg - finalGoalKg).toFixed(1)} kg` : direction === 'gain' ? `Gaining ${(finalGoalKg - finalWeightKg).toFixed(1)} kg` : 'Maintaining'}</p>)}
            </div>
            <div>
              <Label className="text-emerald-950">Activity level</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">Sedentary (little/no exercise)</SelectItem>
                  <SelectItem value="light">Lightly active (1–3 days/week)</SelectItem>
                  <SelectItem value="moderate">Moderately active (3–5 days/week)</SelectItem>
                  <SelectItem value="very">Very active (6–7 days/week)</SelectItem>
                  <SelectItem value="extra">Extra active (hard training + physical job)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {direction !== 'maintain' && (
              <div>
                <Label className="text-emerald-950">Pace</Label>
                <RadioGroup value={pace} onValueChange={setPace} className="grid grid-cols-3 gap-2 mt-2">
                  {[['slow','Gentle','0.25 kg/wk'],['steady','Balanced','0.5 kg/wk'],['aggressive','Intense','0.75 kg/wk']].map(([v,l,d]) => (
                    <label key={v} className={`rounded-xl border p-3 text-center cursor-pointer ${pace===v ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-950/10'}`}><RadioGroupItem value={v} className="sr-only" /><span className="font-medium text-emerald-950 text-sm">{l}</span><span className="block text-[10px] text-emerald-950/50">{d}</span></label>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between pt-4">
          {step > 0 ? <Button variant="outline" className="rounded-full" onClick={() => setStep(step - 1)}>Back</Button> : <span />}
          {step < 2 ? (
            <Button disabled={!canNext()} onClick={() => setStep(step + 1)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">Next <ArrowRight className="w-4 h-4 ml-2" /></Button>
          ) : (
            <Button disabled={!canNext() || saving} onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save & see my target</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
